import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  FileSpreadsheet,
  Presentation,
  FileText,
  Users,
  TrendingUp,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  createTemplate,
  deleteTemplate,
  getRecentDownloads,
  getTemplates,
  Template,
  updateTemplate,
} from "@/data/templates";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TemplateForm } from "@/components/templates/TemplateForm";

interface DownloadRecord {
  email: string;
  date: string;
  template: string;
}

const FileIcon = ({ type }: { type: string }) => {
  if (type === "Excel")
    return <FileSpreadsheet className="h-4 w-4 text-emerald-600" />;
  if (type === "PowerPoint")
    return <Presentation className="h-4 w-4 text-orange-600" />;
  return <FileText className="h-4 w-4 text-blue-600" />;
};

export default function AdminTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [downloaders, setDownloaders] = useState<DownloadRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "" as "financial" | "operations" | "",
    fileType: "" as "Excel" | "PowerPoint" | "Word/PDF" | "",
    tags: "",
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const templatesData = await getTemplates(searchQuery, categoryFilter);
      setTemplates(templatesData);

      const downloadsData = await getRecentDownloads(5);
      setDownloaders(downloadsData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch =
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );
      const matchesCategory = !categoryFilter || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [templates, searchQuery, categoryFilter]);

  const totalDownloads = templates.reduce((sum, t) => sum + t.downloads, 0);
  const weeklyDownloads = templates.reduce(
    (sum, t) => sum + t.lastWeekDownloads,
    0
  );
  const topTemplate = useMemo(
    () => [...templates].sort((a, b) => b.downloads - a.downloads)[0],
    [templates]
  );

  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      description: "",
      category: "",
      fileType: "",
      tags: "",
    });
    setSelectedFile(null);
    setSelectedTemplate(null);
  }, []);

  const handleEdit = useCallback((template: Template) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      description: template.description,
      category: template.category,
      fileType: template.fileType,
      tags: template.tags.join(", "),
    });
    setEditDialogOpen(true);
  }, []);

  const handleDelete = useCallback((template: Template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      setFormLoading(true);
      await deleteTemplate(selectedTemplate.id);
      toast.success(`"${selectedTemplate.title}" deleted`);
      loadData();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setFormLoading(false);
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
    }
  };

  const saveEdit = async () => {
    if (
      !selectedTemplate ||
      !formData.title ||
      !formData.category ||
      !formData.fileType
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setFormLoading(true);

      const fileToUpdate = selectedFile || undefined;

      await updateTemplate(
        selectedTemplate.id,
        {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          fileType: formData.fileType,
          tags: formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
        },
        fileToUpdate
      );

      toast.success(`"${formData.title}" updated`);
      setEditDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAdd = async () => {
    if (
      !formData.title ||
      !formData.category ||
      !formData.fileType ||
      !selectedFile
    ) {
      toast.error("Please fill all fields and select a file");
      return;
    }

    try {
      setFormLoading(true);

      await createTemplate(
        {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          fileType: formData.fileType,
          file_url: "",
          file_name: selectedFile.name,
          tags: formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
          downloads: 0,
          lastWeekDownloads: 0,
          created_at: "",
          updated_at: "",
        },
        selectedFile
      );

      toast.success(`"${formData.title}" created`);
      setAddDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Failed to create template");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
            <p className="text-muted-foreground mt-1">
              Manage downloadable resources and track usage
            </p>
          </div>
          <Dialog
            open={addDialogOpen}
            onOpenChange={(open) => {
              setAddDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-primary-blue">
                <Plus className="mr-2 h-4 w-4" />
                Add Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg p-4">
              <DialogHeader>
                <DialogTitle>Add New Template</DialogTitle>
                <DialogDescription>
                  Create a new downloadable resource for users.
                </DialogDescription>
              </DialogHeader>
              <TemplateForm
                formData={formData}
                setFormData={setFormData}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                formLoading={formLoading}
                handleAdd={handleAdd}
                saveEdit={saveEdit}
                resetForm={resetForm}
                setAddDialogOpen={setAddDialogOpen}
                setEditDialogOpen={setEditDialogOpen}
                selectedTemplate={selectedTemplate}
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-primary">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Downloads
                  </p>
                  <p className="text-2xl font-bold">{totalDownloads}</p>
                </div>
                <div className="flex items-center gap-1 text-emerald-600 text-sm">
                  <TrendingUp className="h-4 w-4" />+{weeklyDownloads} this week
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Top Performer</p>
              <p className="text-lg font-semibold truncate">
                {topTemplate?.title || "No templates"}
              </p>
              <p className="text-sm text-muted-foreground">
                {topTemplate?.downloads || 0} downloads
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Active Templates</p>
              <p className="text-2xl font-bold">{templates.length}</p>
              <p className="text-sm text-muted-foreground">
                {templates.filter((t) => t.category === "financial").length}{" "}
                financial,{" "}
                {templates.filter((t) => t.category === "operations").length}{" "}
                operations
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates or tags..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Categories</SelectItem>
              <SelectItem value="financial">Financial</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Templates</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead className="text-right">Downloads</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-12 text-center text-slate-500 text-base"
                      >
                        No templates found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <FileIcon type={template.fileType} />
                            <div>
                              <p className="font-medium">{template.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {template.fileType} •{" "}
                                {new Date(
                                  template.created_at
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {template.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {template.tags.map((tag) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Download className="h-3 w-3 text-muted-foreground" />
                            {template.downloads}
                            {template.lastWeekDownloads > 5 && (
                              <TrendingUp className="h-3 w-3 text-emerald-600 ml-1" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(template)}
                              disabled={formLoading}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDelete(template)}
                              disabled={formLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Recent Downloads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {downloaders.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-8 text-center text-slate-500"
                      >
                        No downloads yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    downloaders.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{d.email}</TableCell>
                        <TableCell>{d.template}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {d.date}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Update template details and metadata.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[calc(90vh-140px)] px-6 pb-6 minimal-scrollbar">
            <TemplateForm
              isEdit
              formData={formData}
              setFormData={setFormData}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              formLoading={formLoading}
              selectedTemplate={selectedTemplate}
              handleAdd={handleAdd}
              saveEdit={saveEdit}
              resetForm={resetForm}
              setAddDialogOpen={setAddDialogOpen}
              setEditDialogOpen={setEditDialogOpen}
            />
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTemplate?.title}"? This
              action cannot be undone.
              {selectedTemplate && selectedTemplate.downloads > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ This template has {selectedTemplate.downloads} downloads.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={formLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
